import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { Loader, SectionTitle } from '../../shared/components';
import { getWebsiteProjects } from '../website.service';
import WebsiteProjectDisplayCard from './WebsiteProjectDisplayCard';
import WebsiteProjectEntryForm from './WebsiteProjectEntryForm';

export default function WebsiteProjectsWidget() {
  const [formOpen, setFormOpen] = useState(false);
  const [entryMode, setEntryMode] = useState('add');
  const [activeProject, setActiveProject] = useState(null);

  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);

  const allLocations = useMemo(() => {
    const uniqueLocations = new Set();
    projects.forEach((project) => uniqueLocations.add(project.location));
    return Array.from(uniqueLocations);
  }, [projects]);

  const allTags = useMemo(() => {
    const uniqueTags = new Set();
    projects.forEach((project) => project.tags.forEach((tag) => uniqueTags.add(tag)));
    return Array.from(uniqueTags);
  }, [projects]);

  const fetchAllWebsiteProjects = useCallback(async () => {
    setLoading(true);
    setFormOpen(false);
    setActiveProject(null);
    try {
      const projectsData = await getWebsiteProjects();
      setProjects(projectsData);
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to fetch website projects',
          error: err,
          code: 'WEBSITE_FETCH_FAILED',
        })
      );
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllWebsiteProjects();
  }, [fetchAllWebsiteProjects]);

  return (
    <>
      <Stack direction='column' gap={5}>
        <SectionTitle
          title="Website's Data"
          action={
            <button
              type='button'
              className='btn cta-outlined-btn'
              onClick={() => {
                setEntryMode('add');
                setFormOpen(true);
              }}
            >
              Add Project
            </button>
          }
        />

        <Stack
          direction='column'
          gap={2}
          sx={{ maxHeight: '40vh', overflowY: 'auto', scrollbarGutter: 'stable', pr: 1 }}
        >
          {loading ? (
            <Loader />
          ) : projects?.length === 0 ? (
            <Alert severity='info' sx={{ fontStyle: 'italic' }}>
              No projects available
            </Alert>
          ) : (
            projects.map((project, index) => (
              <WebsiteProjectDisplayCard
                key={index}
                project={project}
                index={index}
                onEdit={() => {
                  setEntryMode('edit');
                  setActiveProject(project);
                  setFormOpen(true);
                }}
                onDelete={fetchAllWebsiteProjects}
              />
            ))
          )}
        </Stack>
      </Stack>
      <WebsiteProjectEntryForm
        key={`${entryMode}-${formOpen ? 'open' : 'closed'}`}
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setActiveProject(null);
        }}
        mode={entryMode}
        project={activeProject}
        tags={allTags}
        locations={allLocations}
        onSubmit={fetchAllWebsiteProjects}
      />
    </>
  );
}
